import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { Trans } from "react-i18next";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationSectionHeader } from "web_myplann/Inspiration/Components/InspirationSectionHeader";
import { InspirationStatCard } from "web_myplann/Inspiration/Components/InspirationStatCard";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import { OWN_HOME_EXAMPLE } from "web_myplann/Inspiration/Utils/ownHomeMath";

const PageResources = Resources.Inspiration.OwnHome;

export const OwnHomeColdIntro: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const formatAmount = useFormatGoalAmount();

	const cardBg = theme.palette.background.paper;

	const money = (value: number) => formatAmount(value, INSPIRATION_CURRENCY);

	return (
		<>
			<Stack bgcolor={cardBg} px={1.6} py={2}>
				<Typography
					variant="body1"
					color={theme.palette.grey[700]}
					lineHeight={1.625}
				>
					{t(PageResources.introPrefix)}
					<Typography component="span" variant="body1" fontWeight={600}>
						{t(PageResources.introBold)}
					</Typography>
					{t(PageResources.introSuffix)}
				</Typography>
			</Stack>

			<InspirationSectionHeader title={t(PageResources.exampleTitle)} />

			<Stack bgcolor={cardBg} px={1.6} py={1.6} gap={1.2}>
				<Typography variant="body2" color={theme.palette.grey[600]}>
					<Trans
						i18nKey={getResourcePath(PageResources.exampleBody)}
						values={{
							rent: money(OWN_HOME_EXAMPLE.rent),
							property: money(OWN_HOME_EXAMPLE.property),
						}}
						components={{
							b: <Typography component="span" variant="bodyStrong" tabular />,
							blue: (
								<Typography
									component="span"
									variant="bodyStrong"
									color={theme.palette.blue.main}
									tabular
								/>
							),
						}}
					/>
				</Typography>

				<Stack direction="row" gap={0.8}>
					<InspirationStatCard
						label={t(PageResources.rentCardLabel)}
						amount={money(0)}
						bg={theme.palette.surface.muted}
						labelColor={theme.palette.grey[500]}
						amountColor={theme.palette.red.main}
					/>
					<InspirationStatCard
						label={t(PageResources.ownCardLabel)}
						amount={`~${money(OWN_HOME_EXAMPLE.property)}`}
						bg={theme.palette.blue[50]}
						labelColor={theme.palette.blue.main}
						amountColor={theme.palette.blue.main}
					/>
				</Stack>

				<Typography
					variant="caption"
					color={theme.palette.grey[500]}
					textAlign="center"
				>
					{t(PageResources.exampleHint)}
				</Typography>
			</Stack>
		</>
	);
};
